// frontend/src/pages/Dashboard.jsx
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getAgreements } from '../api/agreement.api.js';
import { getVendors } from '../api/vendor.api.js';
import { Spinner, StatusBadge, formatDate } from '../components/ui';
import { FileText, Users, AlertTriangle, Clock, ChevronRight } from 'lucide-react';

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, icon: Icon, color, to }) {
  const inner = (
    <div className={`card p-5 flex items-center gap-4 ${to ? 'hover:shadow-md transition-shadow' : ''}`}>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon size={22} className="text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value ?? '—'}</p>
        <p className="text-sm text-gray-500">{label}</p>
      </div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

// ─── Agreement row ────────────────────────────────────────────────────────────

function AgreementRow({ agreement, showCounterparty }) {
  const counterparty = agreement.vendor?.companyName ?? agreement.partner?.companyName;
  const daysLeft     = Math.ceil((new Date(agreement.endDate) - Date.now()) / (1000 * 60 * 60 * 24));
  const expiringSoon = agreement.status === 'APPROVED' && daysLeft >= 0 && daysLeft <= 30;

  return (
    <Link
      to={`/agreements/${agreement.id}`}
      className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-gray-900 truncate">{agreement.title}</p>
          {expiringSoon && (
            <span className="badge bg-amber-100 text-amber-700 flex-shrink-0">{daysLeft}d left</span>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-0.5 truncate">
          {showCounterparty && counterparty && <span className="mr-1">{counterparty} ·</span>}
          {agreement.entityCache?.name} · {formatDate(agreement.endDate)}
        </p>
      </div>
      <div className="flex items-center gap-2 ml-4 flex-shrink-0">
        <StatusBadge status={agreement.status} />
        <ChevronRight size={15} className="text-gray-400" />
      </div>
    </Link>
  );
}

// ─── Admin dashboard ──────────────────────────────────────────────────────────

function AdminDashboard() {
  const { data: agreementsRes, isLoading: loadingA } = useQuery({
    queryKey: ['agreements', 'dashboard'],
    queryFn:  () => getAgreements({ limit: 10 }),
  });

  const { data: vendors } = useQuery({
    queryKey: ['vendors', 'count'],
    queryFn:  () => getVendors(),
  });

  const agreements = agreementsRes?.data ?? [];

  const active    = agreements.filter((a) => a.status === 'APPROVED').length;
  const pending   = agreements.filter((a) => ['DRAFT', 'UNDER_REVIEW'].includes(a.status)).length;
  const expiring  = agreements.filter((a) => {
    if (a.status !== 'APPROVED') return false;
    const days = Math.ceil((new Date(a.endDate) - Date.now()) / (1000 * 60 * 60 * 24));
    return days >= 0 && days <= 30;
  }).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">
          {new Date().toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Active Agreements"  value={active}          icon={FileText}      color="bg-green-500"    to="/agreements?status=APPROVED" />
        <StatCard label="Pending Review"     value={pending}         icon={Clock}         color="bg-blue-500"     to="/agreements?status=UNDER_REVIEW" />
        <StatCard label="Expiring (30 days)" value={expiring}        icon={AlertTriangle} color="bg-amber-500" />
        <StatCard label="Total Vendors"      value={vendors?.length} icon={Users}         color="bg-brand-500"    to="/vendors" />
      </div>

      <div className="card">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Recent Agreements</h2>
          <Link to="/agreements" className="text-sm text-brand-500 hover:underline">View all</Link>
        </div>
        {loadingA ? <Spinner /> : (
          <div className="divide-y divide-gray-50">
            {agreements.slice(0, 8).map((a) => (
              <AgreementRow key={a.id} agreement={a} showCounterparty />
            ))}
            {!agreements.length && (
              <p className="px-6 py-8 text-sm text-gray-400 text-center">No agreements yet</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Vendor / partner dashboard ───────────────────────────────────────────────

function CounterpartyDashboard() {
  const { user, isVendor } = useAuth();

  const { data: agreementsRes, isLoading } = useQuery({
    queryKey: ['my-agreements'],
    queryFn:  () => getAgreements({ limit: 10 }),
  });

  const agreements = agreementsRes?.data ?? [];
  const active     = agreements.find((a) => a.status === 'APPROVED');
  const pending    = agreements.filter((a) =>
    ['DRAFT', 'UNDER_REVIEW', 'REVISION_REQUESTED'].includes(a.status)
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome, {user?.name ?? user?.companyName}
        </h1>
        <div className="flex items-center gap-2 mt-1">
          <p className="text-sm text-gray-500 capitalize">{isVendor ? 'Vendor account' : 'Partner account'}</p>
          {isVendor && !user?.isVerified && (
            <span className="badge bg-amber-100 text-amber-700">Pending verification</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <StatCard label="Active Agreement" value={active ? 1 : 0} icon={FileText} color="bg-green-500" />
        <StatCard label="Pending"          value={pending.length}  icon={Clock}    color="bg-blue-500" />
      </div>

      <div className="card">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">My Agreements</h2>
          <Link to="/agreements" className="text-sm text-brand-500 hover:underline">View all</Link>
        </div>
        {isLoading ? <Spinner /> : (
          <div className="divide-y divide-gray-50">
            {agreements.slice(0, 8).map((a) => (
              <AgreementRow key={a.id} agreement={a} showCounterparty={false} />
            ))}
            {!agreements.length && (
              <div className="px-6 py-8 text-center">
                <p className="text-sm text-gray-400 mb-3">No agreements yet</p>
                <Link to="/agreements/new" className="btn-primary text-sm">
                  Submit Agreement
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Export ───────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { isAdmin } = useAuth();
  return isAdmin ? <AdminDashboard /> : <CounterpartyDashboard />;
}
